/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { LowFrictionTrialsStrings } from "../experiments/LftCountdown/LftStrings"
import { TrialConversionStrings } from "../experiments/TrialConversion/TrialConversionStrings"
import { WelcomeStrings } from "../experiments/Welcome/WelcomeStrings"
import { SharedStrings } from "../shared/SharedStrings"

/***************************************************
 * AUTOGENERATED FILE. If new strings are added or
 * removed, new language added or if string IDs are
 * modified, run:
 *
 *   yarn update-strings
 *
 ***************************************************/

const englishStrings: IStringTable = require('../../assets/strings.resjson')
export let strings: IStringTable = englishStrings

export type StrId =
  | WelcomeStrings
  | LowFrictionTrialsStrings
  | TrialConversionStrings
  | SharedStrings

const availableLocales: string[] = [
  'ar-SA',
  'bg-BG',
  'ca-ES',
  'cs-CZ',
  'cy-GB',
  'da-DK',
  'de-DE',
  'el-GR',
  'en-GB',
  'es-ES',
  'es-MX',
  'et-EE',
  'fi-FI',
  'fil-PH',
  'fr-CA',
  'fr-FR',
  'he-IL',
  'hr-HR',
  'hu-HU',
  'id-ID',
  'is-IS',
  'it-IT',
  'ja-JP',
  'ko-KR',
  'lt-LT',
  'lv-LV',
  'ms-MY',
  'mt-MT',
  'nb-NO',
  'nl-NL',
  'pl-PL',
  'pt-BR',
  'pt-PT',
  'ro-RO',
  'ru-RU',
  'sk-SK',
  'sl-SI',
  'sq-AL',
  'sr-Cyrl-RS',
  'sr-Latn-RS',
  'sv-SE',
  'th-TH',
  'tr-TR',
  'uk-UA',
  'vi-VN',
  'zh-CN',
  'zh-TW'
]

interface IStringTable {
  getString(strId: StrId): string
}

const loadStringTable = (baseDomain: string, displayLanguage: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // English strings are default
    if (displayLanguage.toLowerCase() === 'en-us') {
      strings = englishStrings
      resolve()

      return
    }

    // Check if the given language is available
    if (availableLocales.includes(displayLanguage) === false) {
      reject('Language ' + displayLanguage + ' is not available at this time.')

      return
    }

    // Non-english strings require us to load a resource file
    const request: XMLHttpRequest = new XMLHttpRequest()
    const uri = baseDomain + '/intl/' + displayLanguage + '/strings.resjson'
    request.open('GET', uri, true /*async*/)
    request.setRequestHeader('Content-Type', 'application/json')
    request.timeout = 60000 /* 60 seconds */
    request.onreadystatechange = (): void => {
      if (request.readyState === XMLHttpRequest.DONE) {
        if (request.status === 200) {
          // Override our string table with the strings that we pulled from the resource file.
          // Fall back to English strings if the given string is missing from the downloaded resource,
          // e.g. if the string was just added and hasn't been localized yet.
          const localizedStrings = JSON.parse(request.responseText)
          strings = {
            getString: (strid: StrId) =>
              localizedStrings[strid] ? localizedStrings[strid] : englishStrings.getString(strid)
          }
          resolve()

          return
        } else {
          reject('Failed to load ' + displayLanguage + ' strings with status = ' + request.status)
        }
      }
    }

    request.send()
  })
}

export { loadStringTable }
